/**
 * Отказ в заявке агента на выплату или в её исполнении: код и причина → текст
 * на языке пользователя (AD-632).
 *
 * ЗАЧЕМ. Сервер отвечает одним кодом `PAYOUT_NOT_ALLOWED` и причиной в
 * `details.reason`: договора нет, сумма меньше порога, реквизиты не сверены,
 * заявка уже открыта. Серверный `message` одноязычный — у бэкенда нет локали
 * спрашивающего, — поэтому текст выбирается здесь по причине.
 *
 * ГЕЙТ. Ни код, ни причины не переписаны руками: союзы — из генерата
 * (`api/generated/types.gen.ts`), таблицы объявлены литералами с
 * `satisfies Record<…, string>`. Сервер завёл новую причину — не хватает ключа,
 * `tsc` красный; убрал — лишний ключ, `tsc` красный. Ключи живут в `common`,
 * которое компилятор не проверяет, поэтому таблицы названы `*_LABEL_KEYS`:
 * их значения судит `scan:i18n-keys` во всех пяти языках. Приём AD-576
 * (`checkin-refusal-codes.ts`).
 */

import type {
  PayoutAvailabilityDto,
  PayoutRefusalErrorBodyDto,
  PayoutRefusalErrorDetailsDto,
} from "../../api/generated/types.gen";
import {
  extractApiErrorCode,
  extractApiErrorDetails,
} from "../card-gate/card-gate-controller";

export type PayoutRefusalCode = PayoutRefusalErrorBodyDto["code"];
export type PayoutRefusalReason = PayoutRefusalErrorDetailsDto["reason"];
export type PayoutUnavailableReason =
  PayoutAvailabilityDto["payoutUnavailableReason"];

/** Текст на код, когда причина экрану незнакома (сервер новее экрана). */
const PAYOUT_REFUSAL_CODE_LABEL_KEYS = {
  PAYOUT_NOT_ALLOWED: "referral.payoutRefusal.notAllowed",
} as const satisfies Record<PayoutRefusalCode, string>;

/** Текст на каждую причину отказа в заявке или в её исполнении. */
const PAYOUT_REFUSAL_LABEL_KEYS = {
  no_contract: "referral.payoutRefusal.noContract",
  contract_not_signed: "referral.payoutRefusal.contractNotSigned",
  contract_not_started: "referral.payoutRefusal.contractNotStarted",
  contract_suspended: "referral.payoutRefusal.contractSuspended",
  contract_terminated: "referral.payoutRefusal.contractTerminated",
  contract_expired: "referral.payoutRefusal.contractExpired",
  amount_below_minimum: "referral.payoutRefusal.amountBelowMinimum",
  insufficient_balance: "referral.payoutRefusal.insufficientBalance",
  schedule_limit_reached: "referral.payoutRefusal.scheduleLimitReached",
  payout_account_not_verified:
    "referral.payoutRefusal.payoutAccountNotVerified",
  payout_account_changed: "referral.payoutRefusal.payoutAccountChanged",
  open_request_exists: "referral.payoutRefusal.openRequestExists",
  actor_is_agent: "referral.payoutRefusal.actorIsAgent",
  request_not_pending: "referral.payoutRefusal.requestNotPending",
  request_not_awaiting_transfer:
    "referral.payoutRefusal.requestNotAwaitingTransfer",
  request_without_contract: "referral.payoutRefusal.requestWithoutContract",
  legacy_debit_unresolved: "referral.payoutRefusal.legacyDebitUnresolved",
} as const satisfies Record<PayoutRefusalReason, string>;

/**
 * Тот же отказ с числом из `details`. Поля в контракте необязательные, а
 * i18next оставляет непереданную переменную в тексте как есть (`{{available}}`),
 * поэтому фраза с числом выбирается, только когда сервер число прислал; иначе —
 * фраза без числа из таблицы выше.
 */
const PAYOUT_REFUSAL_WITH_VALUE_LABEL_KEYS = {
  amount_below_minimum: "referral.payoutRefusal.amountBelowMinimumWithLimit",
  insufficient_balance:
    "referral.payoutRefusal.insufficientBalanceWithAvailable",
} as const;

/**
 * Почему агенту сейчас нельзя подать заявку — признак профиля. Причины договора
 * — те же слова, что у отказа; `balance_below_minimum` есть только здесь.
 */
const PAYOUT_UNAVAILABLE_LABEL_KEYS = {
  no_contract: "referral.payoutRefusal.noContract",
  contract_not_signed: "referral.payoutRefusal.contractNotSigned",
  contract_not_started: "referral.payoutRefusal.contractNotStarted",
  contract_suspended: "referral.payoutRefusal.contractSuspended",
  contract_terminated: "referral.payoutRefusal.contractTerminated",
  contract_expired: "referral.payoutRefusal.contractExpired",
  payout_account_not_verified:
    "referral.payoutRefusal.payoutAccountNotVerified",
  open_request_exists: "referral.payoutRefusal.openRequestExists",
  schedule_limit_reached: "referral.payoutRefusal.scheduleLimitReached",
  balance_below_minimum: "referral.payoutRefusal.balanceBelowMinimum",
} as const satisfies Record<PayoutUnavailableReason, string>;

export type PayoutRefusalMessageKey =
  | (typeof PAYOUT_REFUSAL_CODE_LABEL_KEYS)[PayoutRefusalCode]
  | (typeof PAYOUT_REFUSAL_LABEL_KEYS)[PayoutRefusalReason]
  | (typeof PAYOUT_REFUSAL_WITH_VALUE_LABEL_KEYS)[keyof typeof PAYOUT_REFUSAL_WITH_VALUE_LABEL_KEYS]
  | (typeof PAYOUT_UNAVAILABLE_LABEL_KEYS)[PayoutUnavailableReason];

/** Ключ текста и значения подстановки (`{{minPayoutAmount}}`, `{{available}}`). */
export interface PayoutRefusalMessage {
  key: PayoutRefusalMessageKey;
  values: Record<string, string | number>;
}

function isPayoutRefusalCode(value: unknown): value is PayoutRefusalCode {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(PAYOUT_REFUSAL_CODE_LABEL_KEYS, value)
  );
}

function isPayoutRefusalReason(value: unknown): value is PayoutRefusalReason {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(PAYOUT_REFUSAL_LABEL_KEYS, value)
  );
}

export function isPayoutUnavailableReason(
  value: unknown,
): value is PayoutUnavailableReason {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(PAYOUT_UNAVAILABLE_LABEL_KEYS, value)
  );
}

/** Ключ текста признака профиля: почему заявку сейчас подать нельзя. */
export function payoutUnavailableMessageKey(
  reason: PayoutUnavailableReason,
): PayoutRefusalMessageKey {
  return PAYOUT_UNAVAILABLE_LABEL_KEYS[reason];
}

/**
 * Текст отказа по заявке или `undefined`, если это не доменный отказ (сбой,
 * 404, ошибка проверки тела) — тогда экран показывает свой общий текст.
 */
export function payoutRefusalMessage(
  err: unknown,
): PayoutRefusalMessage | undefined {
  const code = extractApiErrorCode(err);
  if (!isPayoutRefusalCode(code)) return undefined;

  const details = extractApiErrorDetails(err);
  if (typeof details !== "object" || details === null) {
    return { key: PAYOUT_REFUSAL_CODE_LABEL_KEYS[code], values: {} };
  }
  const reason = "reason" in details ? details.reason : undefined;
  if (!isPayoutRefusalReason(reason)) {
    return { key: PAYOUT_REFUSAL_CODE_LABEL_KEYS[code], values: {} };
  }
  if (reason === "amount_below_minimum") {
    const minPayoutAmount =
      "minPayoutAmount" in details ? details["minPayoutAmount"] : undefined;
    if (typeof minPayoutAmount === "string") {
      return {
        key: PAYOUT_REFUSAL_WITH_VALUE_LABEL_KEYS[reason],
        values: { minPayoutAmount },
      };
    }
  }
  if (reason === "insufficient_balance") {
    const available = "available" in details ? details["available"] : undefined;
    if (typeof available === "number") {
      return {
        key: PAYOUT_REFUSAL_WITH_VALUE_LABEL_KEYS[reason],
        values: { available },
      };
    }
  }
  return { key: PAYOUT_REFUSAL_LABEL_KEYS[reason], values: {} };
}
